export type CrmContactProgressTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmContactProgressMilestone {
  key: string;
  label: string;
  status: string | null;
  value: string | null;
  description: string | null;
  dueAt: string | null;
  completedAt: string | null;
  tone: CrmContactProgressTone;
}

export interface CrmContactProgressSnapshot {
  key: string;
  label: string;
  programLabel: string | null;
  progressPercent: number | null;
  status: string | null;
  summary: string | null;
  updatedAt: string | null;
  tone: CrmContactProgressTone;
  milestones: CrmContactProgressMilestone[];
}

export interface BuildCrmContactProgressSnapshotOptions {
  snapshotKeys?: readonly string[];
  maxMilestones?: number;
}

export const CRM_CONTACT_PROGRESS_SNAPSHOT_FIELD_KEYS = [
  "crmContactProgressSnapshot",
  "contactProgressSnapshot",
  "customerProgressSnapshot",
  "programProgress",
  "learningProgress",
  "courseProgress",
  "onboardingProgress",
  "successProgress",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_TEXT_LENGTH = 180;

export function buildCrmContactProgressSnapshot(
  customFields: unknown,
  options: BuildCrmContactProgressSnapshotOptions = {},
): CrmContactProgressSnapshot | null {
  const fields = asRecord(customFields);
  if (!fields) return null;

  const snapshotKeys = options.snapshotKeys?.length
    ? [...options.snapshotKeys]
    : [...CRM_CONTACT_PROGRESS_SNAPSHOT_FIELD_KEYS];
  const maxMilestones = normalizePositiveInteger(options.maxMilestones, 8);

  for (const key of snapshotKeys) {
    const snapshot = parseProgressSnapshot(key, fields[key], maxMilestones);
    if (snapshot) return snapshot;
  }

  return null;
}

function parseProgressSnapshot(
  fallbackKey: string,
  value: unknown,
  maxMilestones: number,
): CrmContactProgressSnapshot | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const progressPercent = normalizePercent(
    record.progressPercent
    ?? record.percentComplete
    ?? record.completionPercent
    ?? record.completionRate
    ?? record.progress
    ?? record.completion,
  );
  const status = truncate(
    readString(record.status)
    ?? readString(record.progressStatus)
    ?? readString(record.state)
    ?? readString(record.phase),
  );
  const summary = truncate(
    readString(record.summary)
    ?? readString(record.description)
    ?? readString(record.evidence),
  );
  const updatedAt = normalizeTimestamp(
    readString(record.updatedAt)
    ?? readString(record.computedAt)
    ?? readString(record.calculatedAt)
    ?? readString(record.timestamp)
    ?? readString(record.at),
  );
  const milestones = parseProgressMilestones(
    record.milestones
    ?? record.steps
    ?? record.modules
    ?? record.stages
    ?? record.lessons
    ?? record.tasks
    ?? record.checkpoints
    ?? record,
    maxMilestones,
  );

  if (progressPercent === null && !status && !summary && milestones.length === 0) return null;

  return {
    key,
    label: truncate(readString(record.label) ?? readString(record.title) ?? formatLabel(key)) ?? formatLabel(key),
    programLabel: truncate(
      readString(record.programLabel)
      ?? readString(record.program)
      ?? readString(record.track)
      ?? readString(record.course)
      ?? readString(record.product),
    ),
    progressPercent,
    status,
    summary,
    updatedAt,
    tone: normalizeTone(readString(record.tone) ?? status),
    milestones,
  };
}

function parseProgressMilestones(value: unknown, maxMilestones: number): CrmContactProgressMilestone[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseProgressMilestone(`milestone-${index + 1}`, entry))
      .filter((milestone): milestone is CrmContactProgressMilestone => Boolean(milestone))
      .slice(0, maxMilestones);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredSnapshotKeys = new Set([
    "key",
    "label",
    "title",
    "programLabel",
    "program",
    "track",
    "course",
    "product",
    "progressPercent",
    "percentComplete",
    "completionPercent",
    "completionRate",
    "progress",
    "completion",
    "status",
    "progressStatus",
    "state",
    "phase",
    "summary",
    "description",
    "evidence",
    "updatedAt",
    "computedAt",
    "calculatedAt",
    "timestamp",
    "at",
    "tone",
    "milestones",
    "steps",
    "modules",
    "stages",
    "lessons",
    "tasks",
    "checkpoints",
  ]);

  return Object.entries(record)
    .filter(([key]) => !ignoredSnapshotKeys.has(key))
    .map(([key, entry]) => parseProgressMilestone(key, entry))
    .filter((milestone): milestone is CrmContactProgressMilestone => Boolean(milestone))
    .slice(0, maxMilestones);
}

function parseProgressMilestone(fallbackKey: string, value: unknown): CrmContactProgressMilestone | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = record
    ? truncate(readString(record.label) ?? readString(record.title) ?? readString(record.name) ?? formatLabel(key)) ?? formatLabel(key)
    : formatLabel(key);
  const status = record
    ? truncate(readString(record.status) ?? readString(record.state) ?? readString(record.phase))
    : null;
  const milestonePercent = record
    ? normalizePercent(record.progressPercent ?? record.percentComplete ?? record.completionPercent ?? record.progress)
    : null;
  const valueCandidate = record
    ? record.value ?? record.result ?? record.outcome ?? record.current ?? record.target
    : value;
  const formattedValue = formatProgressValue(valueCandidate) ?? (milestonePercent !== null ? `${milestonePercent}%` : null);
  const description = record
    ? truncate(readString(record.description) ?? readString(record.helpText) ?? readString(record.evidence) ?? readString(record.summary))
    : null;
  const dueAt = record
    ? normalizeTimestamp(readString(record.dueAt) ?? readString(record.dueDate) ?? readString(record.targetAt))
    : null;
  const completedAt = record
    ? normalizeTimestamp(readString(record.completedAt) ?? readString(record.completedOn) ?? readString(record.finishedAt))
    : null;

  if (!status && !formattedValue && !description && !dueAt && !completedAt) return null;

  return {
    key,
    label,
    status,
    value: formattedValue,
    description,
    dueAt,
    completedAt,
    tone: normalizeTone(record ? readString(record.tone) ?? status : null),
  };
}

function normalizePercent(value: unknown): number | null {
  const numeric = typeof value === "number"
    ? value
    : typeof value === "string" && value.trim()
      ? Number.parseFloat(value.trim().replace(/%$/, ""))
      : NaN;
  if (!Number.isFinite(numeric)) return null;
  const percent = numeric > 0 && numeric <= 1 ? numeric * 100 : numeric;
  return Math.max(0, Math.min(100, Math.round(percent)));
}

function normalizeTimestamp(value: string | null): string | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return new Date(timestamp).toISOString();
}

function normalizeTone(value: string | null): CrmContactProgressTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "blocked" || value === "failed" || value === "overdue" || value === "at_risk" || value === "risk" || value === "stalled") {
    return "danger";
  }
  if (value === "in_progress" || value === "started" || value === "pending" || value === "watch" || value === "needs_attention") {
    return "warning";
  }
  if (value === "scheduled" || value === "not_started" || value === "queued") return "info";
  if (value === "completed" || value === "complete" || value === "done" || value === "graduated" || value === "certified" || value === "passed" || value === "on_track") {
    return "success";
  }
  return "neutral";
}

function formatProgressValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") return Number.isFinite(value) ? String(Math.round(value * 100) / 100) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}
